<script setup>
/**
 * One trip, as the agent sees it while talking to the customer.
 *
 * Everything judged rather than merely stored comes from the server already decided -
 * `hold.label` is the sentence, `hold.state` the colour, `provider_status_stale` the
 * freshness verdict. None of it is recomputed here, and that is the point: the trip model
 * rounds the hold deadline up and reads the day in the account's reporting timezone, so a
 * panel doing its own date arithmetic would disagree with the sweep and with the automation
 * rules about whether a seat is still ticketable. See app/models/travel_trip.rb.
 *
 * In particular there is no countdown. Travelport gives the ticketing time limit as a DATE
 * with no hour and no zone, so an hours-and-minutes display would be inventing a precision
 * the provider never gave.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { calendarDate, dynamicTime } from 'shared/helpers/timeHelper';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  trip: { type: Object, required: true },
  isRefreshing: { type: Boolean, default: false },
  isAcknowledging: { type: Boolean, default: false },
});

const emit = defineEmits(['refresh', 'acknowledge', 'open']);

const { t } = useI18n();

const STATUS_CLASSES = {
  enquiring: 'bg-n-alpha-2 text-n-slate-11',
  quoted: 'bg-n-blue-3 text-n-blue-11',
  held: 'bg-n-amber-3 text-n-amber-11',
  booked: 'bg-n-teal-3 text-n-teal-11',
  ticketed: 'bg-n-teal-3 text-n-teal-11',
  closed: 'bg-n-alpha-2 text-n-slate-10',
};

// `expiring` and `expired` are the two the agent must not miss; `active` and `unknown` are
// facts worth stating but not worth shouting.
const HOLD_CLASSES = {
  expired: 'bg-n-ruby-3 text-n-ruby-11',
  expiring: 'bg-n-amber-3 text-n-amber-11',
  active: 'bg-n-alpha-2 text-n-slate-11',
  unknown: 'bg-n-alpha-2 text-n-slate-11',
};

const requirements = computed(() => props.trip.requirements || {});

const route = computed(() => {
  const { origin, destination } = requirements.value;
  return origin && destination ? `${origin} → ${destination}` : null;
});

const holdClass = computed(() => HOLD_CLASSES[props.trip.hold?.state]);

// The travel dates as a day, never an instant: a departure date has no hour attached.
const travelDates = computed(() =>
  [requirements.value.departure_date, requirements.value.return_date]
    .filter(Boolean)
    .map(date => calendarDate(date))
    .join(' — ')
);

// A booking we have never checked reads as unknown rather than as fresh: the server
// decides staleness, this only renders which of the two sentences applies.
const freshness = computed(() => {
  if (!props.trip.provider_checked_at)
    return t('TRAVEL.TRIP_PANEL.NEVER_CHECKED');
  return t('TRAVEL.TRIP_PANEL.CHECKED', {
    time: dynamicTime(props.trip.provider_checked_at),
  });
});
</script>

<template>
  <div class="p-3 border rounded-lg border-n-weak bg-n-solid-1">
    <div class="flex items-center gap-2">
      <span
        class="px-2 py-0.5 text-xs font-medium capitalize rounded-full"
        :class="STATUS_CLASSES[trip.status]"
      >
        {{ t(`TRAVEL.TRIP_PANEL.STATUS.${trip.status.toUpperCase()}`) }}
      </span>
      <span
        v-if="trip.record_locator"
        class="font-mono text-xs text-n-slate-11"
      >
        {{ trip.record_locator }}
      </span>
    </div>

    <p v-if="route" class="mt-2 text-sm text-n-slate-12">{{ route }}</p>
    <p v-if="travelDates" class="text-xs text-n-slate-11">{{ travelDates }}</p>

    <!-- The provider's own sentence about the deadline, never a countdown built here. -->
    <p
      v-if="trip.hold?.label"
      class="inline-block px-2 py-1 mt-2 text-xs rounded"
      :class="holdClass"
    >
      {{ trip.hold.label }}
    </p>

    <!-- Travelport never pushes a change, so this alarm is the only way an agent learns
         the itinerary moved. Acknowledgement names the digest actually read. -->
    <div
      v-if="trip.schedule_changed"
      class="p-2 mt-2 text-xs border rounded border-n-amber-6 bg-n-amber-2"
    >
      <p class="text-n-amber-11">
        {{ t('TRAVEL.TRIP_PANEL.SCHEDULE_CHANGED') }}
      </p>
      <Button
        xs
        faded
        slate
        class="mt-2"
        :is-loading="isAcknowledging"
        :label="t('TRAVEL.TRIP_PANEL.ACKNOWLEDGE')"
        @click="emit('acknowledge', trip)"
      />
    </div>

    <!-- Freshness is about what the airline says, so it appears only where there is a
         booking to ask about. On an enquiry or a quote there is nothing to check, and
         "Not checked with the airline yet" reads as an outstanding action that does not
         exist - noise on the majority of cards in a narrow column. -->
    <div
      v-if="trip.record_locator"
      class="flex items-center justify-between gap-2 mt-3"
    >
      <span
        class="text-xs"
        :class="
          trip.provider_status_stale ? 'text-n-amber-11' : 'text-n-slate-10'
        "
      >
        {{ freshness }}
      </span>
      <Button
        xs
        ghost
        slate
        :is-loading="isRefreshing"
        :label="t('TRAVEL.TRIP_PANEL.REFRESH')"
        @click="emit('refresh', trip)"
      />
    </div>

    <Button
      xs
      link
      slate
      class="mt-2"
      :label="t('TRAVEL.TRIP_PANEL.OPEN')"
      @click="emit('open', trip)"
    />
  </div>
</template>
